import { FilterValue } from "../Filters/FilterValue";

export type PodBadgeStyle = {
  label: string;
  color: string;
  backgroundColor: string;
};

/** `name` = API identifier; `label` = UI text; `aliases` = accepted CSV sheet values */
export const POD_PRINTING_TYPE_DEFINITIONS = [
  {
    name: "DTF",
    label: "DTF(Direct-to-film)",
    aliases: ["DTF", "DTF(Direct-to-film)"],
    color: "#7C3AED",
    backgroundColor: "#EDE9FE",
  },
  {
    name: "DTG",
    label: "DTG(Direct-to-garment)",
    aliases: ["DTG", "DTG(Direct-to-garment)"],
    color: "#4338CA",
    backgroundColor: "#E0E7FF",
  },
  {
    name: "Screen print",
    label: "Screen print",
    aliases: ["SCREEN_PRINT", "Screen print"],
    color: "#BE185D",
    backgroundColor: "#FCE7F3",
  },
  {
    name: "UV Print",
    label: "UV Print",
    aliases: ["UV_PRINT", "UV Print"],
    color: "#0F766E",
    backgroundColor: "#CCFBF1",
  },
  {
    name: "LASER",
    label: "Laser",
    aliases: ["LASER", "Laser"],
    color: "#B45309",
    backgroundColor: "#FEF9C3",
  },
  {
    name: "DEBOSSING",
    label: "Debossing",
    aliases: ["DEBOSSING", "Debossing"],
    color: "#065F46",
    backgroundColor: "#D1FAE5",
  },
  {
    name: "EMBOSSING",
    label: "Embossing",
    aliases: ["EMBOSSING", "Embossing"],
    color: "#9D174D",
    backgroundColor: "#FCE7F3",
  },
  {
    name: "SUBLIMATION",
    label: "Sublimation",
    aliases: ["SUBLIMATION", "Sublimation"],
    color: "#1D4ED8",
    backgroundColor: "#DBEAFE",
  },
  {
    name: "EMBROIDERY",
    label: "Embroidery",
    aliases: ["EMBROIDERY", "Embroidery"],
    color: "#C2410C",
    backgroundColor: "#FFEDD5",
  },
  {
    name: "Custom Sticker",
    label: "Custom Sticker",
    aliases: ["CUSTOM_STICKER", "Custom Sticker"],
    color: "#0369A1",
    backgroundColor: "#E0F2FE",
  },
  {
    name: "ETCHING",
    label: "Etching",
    aliases: ["ETCHING", "Etching"],
    color: "#57534E",
    backgroundColor: "#E7E5E4",
  },
  {
    name: "STICKER",
    label: "Sticker",
    aliases: ["STICKER", "Sticker"],
    color: "#57534E",
    backgroundColor: "#E7E5E4",
  },
] as const;

export type PodPrintingTypeDefinition =
  (typeof POD_PRINTING_TYPE_DEFINITIONS)[number];

export const POD_PRINTING_TYPE_OPTIONS: FilterValue[] =
  POD_PRINTING_TYPE_DEFINITIONS.map((item) => ({
    id: item.name,
    value: item.label,
  }));

const findPrintingType = (value: string) => {
  const v = value.trim().toLowerCase();
  if (!v) return undefined;

  return POD_PRINTING_TYPE_DEFINITIONS.find(
    (item) =>
      item.name.toLowerCase() === v ||
      item.label.toLowerCase() === v ||
      item.aliases.some((alias) => alias.toLowerCase() === v),
  );
};

const POD_PRINTING_TYPE_FILTER_IDENTIFIERS: Record<string, string> = {
  DTF: "DTF",
  DTG: "DTG",
  "Screen print": "SCREEN_PRINT",
  "UV Print": "UV_PRINT",
  LASER: "LASER_PRINT",
  DEBOSSING: "DEBOSSING",
  EMBOSSING: "EMBOSSING",
  SUBLIMATION: "SUBLIMATION",
  EMBROIDERY: "EMBROIDERY",
  "Custom Sticker": "CUSTOM_STICKER",
  ETCHING: "ETCHING",
  STICKER: "STICKER",
};

export const toPodPrintingTypeFilterIdentifier = (value: string): string => {
  const name = toPodPrintingTypeName(value);
  return POD_PRINTING_TYPE_FILTER_IDENTIFIERS[name] ?? name;
};

export const getPodPrintingTypeLabel = (name: string): string =>
  findPrintingType(name)?.label ?? name.replace(/_/g, " ");

export const toPodPrintingTypeName = (value: string): string =>
  findPrintingType(value)?.name ?? value.trim();

export const getPodPrintingTypeBadgeStyle = (
  value?: string | null,
): PodBadgeStyle => {
  if (!value) {
    return { label: "Not set", color: "#6B7280", backgroundColor: "#F3F4F6" };
  }
  const item = findPrintingType(value);
  if (item) {
    return {
      label: item.label,
      color: item.color,
      backgroundColor: item.backgroundColor,
    };
  }
  return {
    label: value.replace(/_/g, " "),
    color: "#374151",
    backgroundColor: "#F3F4F6",
  };
};
